/**
 * GHL Middleware - Handles contact creation and management in GoHighLevel
 * Supports custom location IDs, API keys, and field mappings
 */

import { GHLConfig, GHLFieldMapping } from '../config/types';

export interface GHLContact {
  firstName: string;
  lastName?: string;
  email: string;
  phone: string;
  [key: string]: any; // For custom fields
}

export interface GHLContactResponse {
  contactId: string;
  contactCreated: boolean;
  tags?: string[];
}

export interface GHLUpdateContactRequest {
  contactId: string;
  data: Partial<GHLContact>;
  tags?: string[];
  customFields?: Record<string, any>;
}

/**
 * GHL API Client
 */
class GHLClient {
  private locationId: string;
  private apiKey: string;
  private baseUrl = 'https://services.leadconnectorhq.com';

  constructor(locationId: string, apiKey: string) {
    this.locationId = locationId;
    this.apiKey = apiKey;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
        'Version': '2021-07-28',
        'X-Location-Id': this.locationId,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `GHL API error: ${response.status} ${response.statusText}`;
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Search for a contact by email or phone
   */
  async searchContact(email?: string, phone?: string): Promise<GHLContactResponse | null> {
    try {
      const params = new URLSearchParams();
      if (email) params.append('email', email);
      if (phone) params.append('phone', phone);

      const response = await this.request<{ contacts: any[] }>(
        `/contacts/search?${params.toString()}`
      );

      if (response.contacts && response.contacts.length > 0) {
        return {
          contactId: response.contacts[0].id,
          contactCreated: false,
        };
      }

      return null;
    } catch (error) {
      console.error('Error searching GHL contact:', error);
      return null;
    }
  }

  /**
   * Create a new contact
   */
  async createContact(
    contact: GHLContact,
    tags?: string[],
    customFields?: Record<string, any>
  ): Promise<GHLContactResponse> {
    const payload: any = {
      firstName: contact.firstName,
      email: contact.email,
      phone: contact.phone,
    };

    if (contact.lastName) {
      payload.lastName = contact.lastName;
    }

    // Add custom fields
    if (customFields) {
      payload.customFields = customFields;
    }

    // Add tags
    if (tags && tags.length > 0) {
      payload.tags = tags;
    }

    const response = await this.request<{ contact: { id: string } }>(
      '/contacts/',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );

    return {
      contactId: response.contact.id,
      contactCreated: true,
      tags: tags || [],
    };
  }

  /**
   * Update an existing contact
   */
  async updateContact(request: GHLUpdateContactRequest): Promise<GHLContactResponse> {
    const payload: any = {};

    if (request.data.firstName) payload.firstName = request.data.firstName;
    if (request.data.lastName) payload.lastName = request.data.lastName;
    if (request.data.email) payload.email = request.data.email;
    if (request.data.phone) payload.phone = request.data.phone;

    // Add custom fields
    if (request.customFields) {
      payload.customFields = request.customFields;
    }

    // Add tags (this will append to existing tags)
    if (request.tags && request.tags.length > 0) {
      payload.tags = request.tags;
    }

    const response = await this.request<{ contact: { id: string } }>(
      `/contacts/${request.contactId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    );

    return {
      contactId: response.contact.id,
      contactCreated: false,
      tags: request.tags || [],
    };
  }

  /**
   * Add tags to a contact
   */
  async addTags(contactId: string, tags: string[]): Promise<void> {
    try {
      await this.request(
        `/contacts/${contactId}/tags`,
        {
          method: 'POST',
          body: JSON.stringify({ tags }),
        }
      );
    } catch (error) {
      console.error('Error adding tags to GHL contact:', error);
      throw error;
    }
  }
}

/**
 * GHL Middleware - Maps form data to GHL custom fields and manages contacts
 */
export class GHLMiddleware {
  private client: GHLClient;
  private config: GHLConfig;
  private fieldMapping: GHLFieldMapping;

  constructor(config: GHLConfig) {
    this.config = config;
    this.client = new GHLClient(config.locationId, config.apiKey);
    this.fieldMapping = config.fieldMapping;
  }

  /**
   * Map form data to GHL custom fields using the field mapping
   */
  private mapFieldsToGHL(formData: Record<string, any>): Record<string, any> {
    const customFields: Record<string, any> = {};

    for (const [formField, ghlField] of Object.entries(this.fieldMapping)) {
      if (formData[formField] !== undefined && formData[formField] !== null) {
        customFields[ghlField] = formData[formField];
      }
    }

    return customFields;
  }

  /**
   * Create or update a contact in GHL
   */
  async saveContact(
    contact: GHLContact,
    formData?: Record<string, any>,
    tags?: string[]
  ): Promise<GHLContactResponse> {
    // Search for existing contact
    const existing = await this.client.searchContact(contact.email, contact.phone);

    // Map form data to GHL custom fields
    const customFields = formData ? this.mapFieldsToGHL(formData) : undefined;

    // Determine tags to use
    const tagsToUse = tags || this.config.defaultTags || [];

    if (existing) {
      // Update existing contact
      return this.client.updateContact({
        contactId: existing.contactId,
        data: contact,
        customFields,
        tags: tagsToUse,
      });
    } else {
      // Create new contact
      return this.client.createContact(contact, tagsToUse, customFields);
    }
  }

  /**
   * Add tags to a contact
   */
  async addTagsToContact(contactId: string, tags: string[]): Promise<void> {
    await this.client.addTags(contactId, tags);
  }

  /**
   * Get contact by ID
   */
  async getContact(contactId: string): Promise<GHLContact | null> {
    try {
      const response = await this.client.request<{ contact: GHLContact }>(
        `/contacts/${contactId}`
      );
      return response.contact;
    } catch (error) {
      console.error('Error getting GHL contact:', error);
      return null;
    }
  }
}

/**
 * Create a GHL middleware instance from config
 */
export function createGHLMiddleware(config: GHLConfig): GHLMiddleware {
  return new GHLMiddleware(config);
}
